import { useCatalogCounts } from '@/features/publicsite/catalogCounts';
import { HomeCalendars } from '@/features/publicsite/home/HomeCalendars';
import { HomeCatalog } from '@/features/publicsite/home/HomeCatalog';
import { HomeFocus } from '@/features/publicsite/home/HomeFocus';
import { HomeFunding } from '@/features/publicsite/home/HomeFunding';
import { HomeHero } from '@/features/publicsite/home/HomeHero';
import { HomeMapTeaser } from '@/features/publicsite/home/HomeMapTeaser';
import { HomeNews } from '@/features/publicsite/home/HomeNews';
import { HomeStats } from '@/features/publicsite/home/HomeStats';
import {
  heroImageSrc,
  homeLanguageFallback,
  SITE_HOME_URL,
} from '@/features/publicsite/home/homeSection';
import type { SiteHome } from '@/features/publicsite/types/home/SiteHome';
import { usePageLanguageFallback } from '@/features/publicsite/usePageLanguageFallback';
import { usePublicData } from '@/features/publicsite/usePublicList';
import { useLanguage } from '@/i18n/LanguageContext';

/**
 * The public home page.
 *
 * Eight sections, all of which existed as one 332-line template: hero with the account panel, the
 * "agriculture in focus" strip, the catalogue grid, the map teaser, the two calendars, the news
 * strip, the funding call-to-action and the usage figures.
 *
 * Each section is its own component under home/, because they have nothing to say to each other:
 * a section owns the data it shows, the dictionary it reads and the state it keeps, and the only
 * thing this file decides is which sections there are and in what order. It was 624 lines with all
 * nine inlined, which is the length at which finding the strip you meant to change stops being
 * free.
 *
 * The order is load-bearing in one respect: the backgrounds alternate, `section` against
 * `home-section--white`, so moving a section means moving its background with it.
 *
 * A ninth section, the three most recent scientific publications (FR-019), stood between the news
 * strip and the funding call-to-action and was removed at the client's request. The results it
 * showed are still published — the research catalogue and each project's own page carry them — so
 * what went is the home page's shortcut to them, not the requirement's data.
 *
 * `counts` is the exception to sections owning their own data — the hero's figures, the catalogue
 * grid's per-category totals and the closing usage strip all print the same numbers. One request
 * answers all three, exactly as the shared `_catalog-cards.html` fragment arranged before the
 * React port; three components fetching it separately would be three answers to one question.
 *
 * The administered section (V108) is the second exception, for the same reason: its texts and cards
 * are the focus strip's, its picture is the hero's. Null while it loads and when it cannot be read,
 * and both sections then show the dictionary's texts and their static pictures.
 */
export function HomePage() {
  const counts = useCatalogCounts();
  const { language } = useLanguage();
  const home = usePublicData<SiteHome | null>(SITE_HOME_URL, null);
  usePageLanguageFallback(homeLanguageFallback(home, language));

  return (
    <>
      <HomeHero counts={counts} heroImage={heroImageSrc(home)} />
      <HomeFocus home={home} />
      <HomeCatalog counts={counts} />
      <HomeMapTeaser />
      <HomeCalendars />
      <HomeNews />
      <HomeFunding />
      <HomeStats counts={counts} />
    </>
  );
}
